import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { ChevronDown, UserMinus } from "lucide-react";
import { createPortal } from "react-dom";

import {
  BulkTransferModal,
  DormantHoleModal,
  successorOptionsHtml,
} from "./DormantHoleModal";
import { getCoverageMatrixDotColor } from "./relationshipUtils";
import { VendorRowLabel } from "./vendor-team-matrix-ui";
import Button from "../../../Components/Button";
import CommonModal from "../../../Components/Modal";
import { useAppDispatch } from "../../../Store/Hooks";
import {
  dismissCoverageHoles,
  fetchDepartedRows,
  fetchSuccessorCandidates,
  transferCoveragePairs,
} from "../../../Store/Reducers/TeamViewsSlice";

const ATM_ROLE_KEYS = new Set(["AE", "CSM", "SE", "TAM", "BCS", "SDR", "VP"]);

/** Nested confirm: Cancel (outline) + Proceed (primary). */
function DepartedConfirmModal({
  open,
  title = "Confirm",
  message,
  confirmLabel = "Proceed",
  cancelLabel = "Cancel",
  loading = false,
  onCancel,
  onConfirm,
}) {
  if (!open) return null;
  return (
    <CommonModal
      isOpen={open}
      toggle={onCancel}
      onCloseButton={onCancel}
      size="xl"
      showHeader={false}
      nested
      usePortal
    >
      <div className="px-1 py-1">
        <h3 className="mb-3 text-[1.22rem] font-semibold leading-tight text-gray-900 md:text-[1.4rem]">
          {title}
        </h3>
        <p className="mb-6 whitespace-pre-line text-[0.94rem] leading-[1.42] text-gray-600 md:text-[1rem]">
          {message}
        </p>
        <div className="flex items-center justify-end gap-3">
          <Button
            variant="outline-primary"
            onClick={onCancel}
            disabled={loading}
            className="min-h-[2.5rem] px-6 text-[1rem]"
          >
            {cancelLabel}
          </Button>
          <Button
            variant="primary"
            onClick={onConfirm}
            disabled={loading}
            className="min-h-[2.5rem] px-6 text-[1rem] font-semibold"
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </CommonModal>
  );
}

/** Nested info alert replacement — single primary dismiss. */
function DepartedInfoModal({ open, title = "Notice", message, onClose }) {
  if (!open) return null;
  return (
    <CommonModal
      isOpen={open}
      toggle={onClose}
      onCloseButton={onClose}
      size="xl"
      showHeader={false}
      nested
      usePortal
    >
      <div className="px-1 py-1">
        <h3 className="mb-3 text-[1.22rem] font-semibold leading-tight text-gray-900 md:text-[1.4rem]">
          {title}
        </h3>
        <p className="mb-6 whitespace-pre-line text-[0.94rem] leading-[1.42] text-gray-600 md:text-[1rem]">
          {message}
        </p>
        <div className="flex items-center justify-end gap-3">
          <Button
            variant="primary"
            onClick={onClose}
            className="min-h-[2.5rem] px-6 text-[1rem] font-semibold"
          >
            OK
          </Button>
        </div>
      </div>
    </CommonModal>
  );
}

function daysSinceDeparted(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const ms = Date.now() - d.getTime();
  return Math.max(0, Math.round(ms / 86400000));
}

function departedPersonAsVendor(person) {
  const roleKey = String(person.role_key || person.role_name || "")
    .trim()
    .toUpperCase();
  const isTeam = ATM_ROLE_KEYS.has(roleKey);
  // Job title under the name comes from organization_users.position.
  const position = String(person.position || "").trim();
  return {
    id: person.user_id,
    name: person.full_name || "Departed",
    member_name: person.full_name || "Departed",
    member_title: position,
    title: position,
    role: roleKey || person.role_name,
    vendorType: isTeam ? "team_member" : "executive",
    __vendorType: isTeam ? "account_team_member" : "executive",
  };
}

/** Compact custom menu — avoids native <select> optgroup cropping. */
function DepartedTransferMenu({ recommended, others, optionLabel, onPick }) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const menuWidth = 220;
    const menuHeight = menuRef.current?.offsetHeight || 200;
    const gap = 4;
    const pad = 8;
    let top = rect.bottom + gap;
    if (top + menuHeight > window.innerHeight - pad) {
      const above = rect.top - menuHeight - gap;
      if (above >= pad) top = above;
    }
    let left = rect.left;
    left = Math.min(left, window.innerWidth - menuWidth - pad);
    left = Math.max(pad, left);
    setMenuPos({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    const frame = requestAnimationFrame(updatePosition);
    return () => cancelAnimationFrame(frame);
  }, [open, updatePosition, recommended.length, others.length]);

  useEffect(() => {
    if (!open) return;
    const onPointer = event => {
      const target = event.target;
      if (
        triggerRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return;
      }
      close();
    };
    const onKey = event => {
      if (event.key === "Escape") close();
    };
    const onReposition = () => updatePosition();
    const t = window.setTimeout(() => {
      document.addEventListener("mousedown", onPointer);
    }, 0);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onReposition, true);
    window.addEventListener("resize", onReposition);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onReposition, true);
      window.removeEventListener("resize", onReposition);
    };
  }, [open, close, updatePosition]);

  const renderGroup = (label, list) => {
    if (!list.length) return null;
    return (
      <div className="departed-transfer-group" key={label}>
        <div className="departed-transfer-group-label">{label}</div>
        {list.map(c => (
          <button
            key={c.user_id}
            type="button"
            className="departed-transfer-option"
            onClick={() => {
              onPick(c.user_id);
              close();
            }}
          >
            {optionLabel(c)}
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className="departed-transfer-menu">
      <button
        ref={triggerRef}
        type="button"
        className={`departed-transfer-trigger${open ? " is-open" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen(prev => !prev)}
      >
        <span>Transfer all</span>
        <ChevronDown size={12} aria-hidden />
      </button>
      {open
        ? createPortal(
            <div
              ref={menuRef}
              className="departed-transfer-panel"
              style={{ top: menuPos.top, left: menuPos.left }}
              role="listbox"
            >
              {renderGroup("Recommended", recommended)}
              {renderGroup("Other owners", others)}
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

function DepartedHoleCell({ hole, onOpen, coverageColumn = false }) {
  const tier = hole.orphan_tier === 2 ? 2 : 3;
  const hasRl = hole.latest_rl != null;
  const dotColor = hasRl
    ? getCoverageMatrixDotColor(hole.latest_rl)
    : "#cbd5e1";
  const label = hasRl ? `RL ${hole.latest_rl}` : "Hole";
  const title =
    tier === 3
      ? "Orphaned — needs a new owner"
      : "Resolved — successor already active";

  return (
    <td
      className={`buddy-cell-td departed-hole-td${coverageColumn ? " mx-hl" : ""}`}
    >
      <button
        type="button"
        className={`mcell cl-hole${tier === 3 ? " cl-hole-tier3" : " cl-hole-tier2"}`}
        onClick={() => onOpen(hole)}
        title={title}
      >
        <div className="rl-dot" style={{ background: dotColor }} aria-hidden />
        <div className="rl-dot-label">{label}</div>
      </button>
    </td>
  );
}

function DepartedSectionHeader({ count, colSpan, highlightColIndexes }) {
  const emptyCols = Array(colSpan)
    .fill(0)
    .map((_, i) => (
      <td
        key={`dep-empty-${i}`}
        className={
          highlightColIndexes?.has(i)
            ? "section-hdr-empty mx-hl"
            : "section-hdr-empty"
        }
      />
    ));
  return (
    <tr className="section-hdr departed-section-hdr">
      <td>
        <div className="section-hdr-label">
          <UserMinus
            size={14}
            style={{ color: "var(--text-muted)", flexShrink: 0 }}
          />
          Departed
          <span
            style={{
              fontSize: 10,
              fontWeight: 500,
              color: "var(--text-muted)",
              marginLeft: 2,
            }}
          >
            ({count})
          </span>
        </div>
      </td>
      {emptyCols}
    </tr>
  );
}

export function DepartedRowSection({
  accountId,
  departed,
  contacts,
  columnDefs: columnDefsProp,
  onCoverageChanged,
  highlightContactIds,
  highlightOwnerId,
  highlightColIndexes,
}) {
  const dispatch = useAppDispatch();
  const [holeModal, setHoleModal] = useState(null);
  const [candidatesByPerson, setCandidatesByPerson] = useState({});
  const [bulkState, setBulkState] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [infoDialog, setInfoDialog] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);

  const columnDefs = useMemo(() => {
    if (columnDefsProp?.length) return columnDefsProp;
    return (contacts || []).map(c => ({
      key: c.id || c.user_id || c.userId,
      contact: c,
      isGhost: false,
    }));
  }, [columnDefsProp, contacts]);

  const dataColCount = Math.max(columnDefs.length, 1);

  const loadCandidates = useCallback(
    async person => {
      if (!accountId || !person?.user_id) return [];
      if (candidatesByPerson[person.user_id]) {
        return candidatesByPerson[person.user_id];
      }
      const data = await dispatch(
        fetchSuccessorCandidates({
          accountId,
          departingUserId: person.user_id,
        })
      ).unwrap();
      const list = data?.candidates || [];
      setCandidatesByPerson(prev => ({ ...prev, [person.user_id]: list }));
      return list;
    },
    [accountId, candidatesByPerson, dispatch]
  );

  useEffect(() => {
    (departed || []).forEach(person => {
      loadCandidates(person);
    });
  }, [departed, loadCandidates]);

  const refresh = () => dispatch(fetchDepartedRows(accountId));

  const handleOpenHole = async (person, hole) => {
    const candidates = await loadCandidates(person);
    setHoleModal({ person, hole, candidates });
  };

  const handleTransfer = ({ hole, successorId, person }) => {
    const successor = (holeModal?.candidates || []).find(
      c => String(c.user_id) === String(successorId)
    );
    const name = successor?.full_name || "successor";
    const contact = hole?.contact_name || "contact";
    setConfirmDialog({
      title: "Confirm transfer",
      message:
        `Transfer ${person.full_name}'s coverage on ${contact} to ${name}? ` +
        "This clears the hole here — history stays preserved on the dormant record.",
      onConfirm: async () => {
        setConfirmBusy(true);
        try {
          const result = await dispatch(
            transferCoveragePairs({
              accountId,
              transfers: [
                {
                  relationship_id: hole.relationship_id,
                  successor_user_id: successorId,
                },
              ],
            })
          ).unwrap();
          setConfirmDialog(null);
          setHoleModal(null);
          await refresh();
          await onCoverageChanged?.();
          if (result?.skipped > 0) {
            setInfoDialog({
              title: "Transfer skipped",
              message:
                "Transfer skipped — that teammate already covers this contact.",
            });
          }
        } finally {
          setConfirmBusy(false);
        }
      },
    });
  };

  const handleDismiss = hole => {
    setConfirmDialog({
      title: "Dismiss hole",
      message:
        "Dismiss this hole? History stays preserved — this only removes it from the Departed Row.",
      onConfirm: async () => {
        setConfirmBusy(true);
        try {
          await dispatch(
            dismissCoverageHoles({
              accountId,
              relationshipIds: [hole.relationship_id],
            })
          ).unwrap();
          setConfirmDialog(null);
          setHoleModal(null);
          await refresh();
        } finally {
          setConfirmBusy(false);
        }
      },
    });
  };

  const handleDismissAll = (person, ids) => {
    if (!ids.length) return;
    setConfirmDialog({
      title: "Dismiss all holes",
      message: `Dismiss all ${ids.length} holes for ${person.full_name}? History is preserved.`,
      onConfirm: async () => {
        setConfirmBusy(true);
        try {
          await dispatch(
            dismissCoverageHoles({
              accountId,
              relationshipIds: ids,
            })
          ).unwrap();
          setConfirmDialog(null);
          await refresh();
        } finally {
          setConfirmBusy(false);
        }
      },
    });
  };

  const handleBulkPick = async (person, successorId) => {
    if (!successorId) return;
    const candidates = await loadCandidates(person);
    const successor = candidates.find(
      c => String(c.user_id) === String(successorId)
    );
    if (!successor) return;
    setBulkState({ person, successor, holes: person.holes || [] });
  };

  const handleBulkConfirm = async transfers => {
    if (!transfers?.length) {
      setBulkState(null);
      return;
    }
    const result = await dispatch(
      transferCoveragePairs({ accountId, transfers })
    ).unwrap();
    setBulkState(null);
    await refresh();
    await onCoverageChanged?.();
    if (result?.skipped > 0) {
      setInfoDialog({
        title: "Transfer skipped",
        message: `${result.skipped} relationship${result.skipped !== 1 ? "s" : ""} skipped — successor already covers those contacts.`,
      });
    }
  };

  if (!departed?.length) return null;

  return (
    <>
      <DepartedSectionHeader
        count={departed.length}
        colSpan={dataColCount}
        highlightColIndexes={highlightColIndexes}
      />

      {departed.map(person => {
        const candidates = candidatesByPerson[person.user_id] || [];
        const { recommended, others, option } = successorOptionsHtml(
          candidates,
          null,
          { excludeCollisions: false }
        );
        const days = daysSinceDeparted(person.departed_at);
        const vendor = departedPersonAsVendor(person);
        const hasCandidates = recommended.length + others.length > 0;

        return (
          <tr
            key={person.user_id}
            className={
              highlightOwnerId &&
              String(person.user_id) === String(highlightOwnerId)
                ? "cl-departed-row mx-hl-row"
                : "cl-departed-row"
            }
          >
            <td>
              <div className="cl-departed-vrow" style={{ opacity: 0.7 }}>
                <VendorRowLabel vendor={vendor} variant="matrix" />
                <div className="cl-departed-actions">
                  <span className="cl-departed-ago">
                    Departed {days != null ? `${days}d ago` : "recently"}
                  </span>
                  <button
                    type="button"
                    className="departed-dismiss-all"
                    onClick={() => {
                      const ids = (person.holes || []).map(
                        h => h.relationship_id
                      );
                      handleDismissAll(person, ids);
                    }}
                  >
                    Dismiss all
                  </button>
                  {hasCandidates ? (
                    <DepartedTransferMenu
                      recommended={recommended}
                      others={others}
                      optionLabel={option}
                      onPick={successorId =>
                        handleBulkPick(person, successorId)
                      }
                    />
                  ) : null}
                </div>
              </div>
            </td>
            {columnDefs.map((col, colIndex) => {
              const coverageColumn = Boolean(
                highlightColIndexes?.has(colIndex) ||
                  (col.contact?.id &&
                    highlightContactIds?.has(String(col.contact.id)))
              );
              if (col.isGhost) {
                return (
                  <td
                    key={col.key}
                    className={
                      coverageColumn ? "mcol-ghost mx-hl" : "mcol-ghost"
                    }
                  >
                    <div className="mcell unassessed" style={{ minHeight: 42 }}>
                      <span style={{ fontSize: 10, color: "var(--gray-300)" }}>
                        —
                      </span>
                    </div>
                  </td>
                );
              }
              const contactId = col.contact?.id || col.key;
              const hole = (person.holes || []).find(
                h => String(h.contact_id) === String(contactId)
              );
              if (!hole) {
                return (
                  <td
                    key={col.key}
                    className={
                      coverageColumn ? "mcol-ghost mx-hl" : "mcol-ghost"
                    }
                  >
                    <div className="mcell unassessed" style={{ minHeight: 42 }}>
                      <span style={{ fontSize: 10, color: "var(--gray-300)" }}>
                        —
                      </span>
                    </div>
                  </td>
                );
              }
              return (
                <DepartedHoleCell
                  key={hole.relationship_id}
                  hole={hole}
                  coverageColumn={coverageColumn}
                  onOpen={() => handleOpenHole(person, hole)}
                />
              );
            })}
          </tr>
        );
      })}

      <DormantHoleModal
        open={Boolean(holeModal)}
        onClose={() => setHoleModal(null)}
        accountId={accountId}
        person={holeModal?.person}
        hole={holeModal?.hole}
        candidates={holeModal?.candidates || []}
        onTransfer={handleTransfer}
        onDismiss={handleDismiss}
      />

      <BulkTransferModal
        open={Boolean(bulkState)}
        onClose={() => setBulkState(null)}
        person={bulkState?.person}
        successor={bulkState?.successor}
        holes={bulkState?.holes || []}
        onConfirm={handleBulkConfirm}
      />

      <DepartedConfirmModal
        open={Boolean(confirmDialog)}
        title={confirmDialog?.title}
        message={confirmDialog?.message || ""}
        loading={confirmBusy}
        onCancel={() => {
          if (confirmBusy) return;
          setConfirmDialog(null);
        }}
        onConfirm={() => confirmDialog?.onConfirm?.()}
      />

      <DepartedInfoModal
        open={Boolean(infoDialog)}
        title={infoDialog?.title}
        message={infoDialog?.message || ""}
        onClose={() => setInfoDialog(null)}
      />
    </>
  );
}

export default DepartedRowSection;
